import React, { useMemo, useRef, useState } from 'react';
import '../css/OnboardingModal.css';   // your existing styles for onboarding
import '../css/Forms.css'; // reuses the "po-*" progress/footer styles

export default function OnboardingModal({ isOpen, onClose }) {
  const formRef = useRef(null);

  // ---------- Draft persistence ----------
  const draftKey = 'ONBOARDING_DRAFT_V2';
  const loadJSON = (k, f = null) => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : f; } catch { return f; } };
  const saveJSON = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} };

  const makeInitial = () => ({
    // Section 1: Engagement Model
    engagementModel: '',
    expectedTalents: 0,
    preferredStartDate: '',
    engagementDuration: 'Mid-Term [6–12 months]',

    // Section 2: Background Checks & Verification
    background: {
      identity: false, govBenefits: false, tin: false, criminal: false,
      employment: false, education: false, reference: false,
      watchlist: false, social: false, credit: false,
    },
    customVerificationRequests: '',

    // Section 3: Medical Screening
    medical: {
      cbc: false, urinalysis: false, xray: false, stool: false, drug: false, pe: false,
    },
    medicalLocation: 'Exodia Facility',

    // Section 4: Employment Documents
    jurisdiction: 'Philippines',
    jurisdictionOther: '',
    clientPolicyFile: null,

    // Section 5: Work Enablement/Setup
    workstation: 'null',   // 'standard' | 'premium'
    itAccess: 'null',      // 'standard' | 'premium'
    workArrangement: 'Remote',
    shiftRequirement: 'Day',
    toolsProvidedByClient: 'No',
    toolsProvidedDetails: '',

    // Section 6: Optional Add-Ons
    addons: {
      fastTrack: false,
      whiteLabelBranding: false,
      psychologicalExam: false,
      hivHepBScreening: false,
    },
    customAddOnRequests: '',

    // Section 7: Notes/Special Instructions
    notes: '',
    complianceSetupNeeds: '',
    pointOfContact: {
      fullName: '',
      email: '',
      phoneWhatsapp: '',
    },
  });

  const [form, setForm] = useState(() => loadJSON(draftKey, makeInitial()));
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(0);

  // ---------- Steps & progress ----------
  const STEPS = useMemo(() => ([
    { key: 'engagement', title: 'Engagement Model' },
    { key: 'background', title: 'Background Checks & Verification' },
    { key: 'medical',    title: 'Medical Screening' },
    { key: 'docs',       title: 'Employment Documents (Auto-Included)' },
    { key: 'setup',      title: 'Work Enablement / Setup' },
    { key: 'addons',     title: 'Optional Add-Ons' },
    { key: 'notes',      title: 'Notes / Special Instructions' },
  ]), []);
  const totalSteps = STEPS.length;
  const progressPct = Math.round(((step + 1) / totalSteps) * 100);

  // ---------- Helpers ----------
  const deepClone = (obj) => (typeof structuredClone === 'function' ? structuredClone(obj) : JSON.parse(JSON.stringify(obj)));

  const setField = (path, value) => {
    setForm((f) => {
      const clone = deepClone(f);
      const parts = path.split('.');
      let cur = clone;
      for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]];
      cur[parts.at(-1)] = value;
      return clone;
    });
  };

  const saveDraft = () => {
    saveJSON(draftKey, form);
    alert('Draft saved ✨');
  };

  const clearDraft = () => {
    try { localStorage.removeItem(draftKey); } catch {}
    setForm(makeInitial());
    setErrors({});
    setStep(0);
    alert('Draft cleared.');
  };

  // ---------- Validation per step (lightweight) ----------
  const validateStep = (s) => {
    const e = {};
    if (s === 0) {
      if (!form.engagementModel) e.engagementModel = 'Please select an engagement model.';
      if (!form.expectedTalents || form.expectedTalents < 1) e.expectedTalents = 'Please enter a valid number of talents.';
      if (!form.preferredStartDate) e.preferredStartDate = 'Please select a preferred start date.';
      if (!form.engagementDuration) e.engagementDuration = 'Please select an engagement duration.';
    }
    if (s === 2) {
      if (!form.medicalLocation) e.medicalLocation = 'Please select a medical screening location.';
    }
    if (s === 3) {
      if (!form.jurisdiction) e.jurisdiction = 'Please select a contract jurisdiction.';
      if (form.jurisdiction === 'Other' && !form.jurisdictionOther.trim()) e.jurisdictionOther = 'Please specify the jurisdiction.';
    }
    if (s === 4) {
      if (!form.workArrangement) e.workArrangement = 'Please select a work arrangement.';
      if (!form.shiftRequirement) e.shiftRequirement = 'Please select a shift requirement.';
      if (!form.toolsProvidedByClient) e.toolsProvidedByClient = 'Please specify if tools are provided by client.';
      if (form.toolsProvidedByClient === 'Yes' && !form.toolsProvidedDetails.trim()) e.toolsProvidedDetails = 'Please specify the tools provided.';
      if (form.workstation !== 'standard' && form.workstation !== 'premium') e.workstation = 'Choose a workstation setup.';
      if (form.itAccess   !== 'standard' && form.itAccess   !== 'premium') e.itAccess   = 'Choose an IT access level.';
    }
    if (s === 6) {
      if (!form.complianceSetupNeeds || !form.complianceSetupNeeds.trim()) e.complianceSetupNeeds = 'Please specify compliance/setup needs.';
      if (!form.pointOfContact.fullName || !form.pointOfContact.fullName.trim()) e.pointOfContactFullName = 'Please enter the contact person\'s full name.';
      if (!form.pointOfContact.email || !form.pointOfContact.email.trim()) e.pointOfContactEmail = 'Please enter a valid email address.';
      if (!form.pointOfContact.phoneWhatsapp || !form.pointOfContact.phoneWhatsapp.trim()) e.pointOfContactPhone = 'Please enter a phone/WhatsApp number.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const goNext = () => { if (validateStep(step)) setStep((s) => Math.min(totalSteps - 1, s + 1)); };
  const goBack = () => setStep((s) => Math.max(0, s - 1));

  const onSubmit = (e) => {
    e.preventDefault();
    if (step !== totalSteps - 1) return;         // submit only on last step
    if (!validateStep(step)) return;
    // Save final payload (swap for API call if needed)
    const all = loadJSON('ORF_v1', []);
    all.push({ ...form, savedAt: new Date().toISOString() });
    saveJSON('ORF_v1', all);
    alert('Onboarding Request Form saved.');
    onClose?.();
  };

  // prevent Enter from submitting mid-wizard (except textareas)
  const preventEnterSubmit = (e) => {
    if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA' && step < totalSteps - 1) {
      e.preventDefault();
    }
  };

  if (!isOpen) return null;

  // ---------- Tiny UI helpers ----------
  const Check = ({ label, checked, onChange, id }) => (
    <label className="orf-check">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} id={id} />
      <span className="orf-checkmark" /> {label}
    </label>
  );

  const Radio = ({ name, value, selected, onChange, label }) => (
    <label className="orf-radio">
      <input type="radio" name={name} value={value} checked={selected === value} onChange={() => onChange(value)} />
      <span className="orf-radiomark" /> {label}
    </label>
  );

  return (
    <div className="orf-overlay" onClick={onClose}>
      <div className="orf-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="orf-header">
          <h2 className="orf-title">Onboarding Request Form</h2>
          <button className="orf-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {/* Optional: reuse your po-progress styles */}
        <div className="po-progress">
          <div className="po-progress-top">
            <div className="po-progress-num">{progressPct}%</div>
          </div>
          <div className="po-progress-bar">
            <div className="po-progress-fill" style={{ width: `${progressPct}%` }} />
          </div>
        </div>

        <form ref={formRef} className="orf-body" onSubmit={onSubmit} onKeyDown={preventEnterSubmit}>
          {/* ====== STEP 1: Engagement Model ====== */}
          {step === 0 && (
            <section className="orf-card">
              <h3 className="orf-card-title">Section 1: Engagement Model <span className="req-star">*</span></h3>
              <div className="orf-grid-1">
                <Radio
                  name="engagement"
                  value="eor-standard"
                  selected={form.engagementModel}
                  onChange={(v) => setField('engagementModel', v)}
                  label="EOR Standard – $1,500 per talent (full suite under our employee-of-record structure)"
                />
                <Radio
                  name="engagement"
                  value="eor-premium"
                  selected={form.engagementModel}
                  onChange={(v) => setField('engagementModel', v)}
                  label="EOR Premium – Contact Sales (customized onboarding, deployment, hardware/software)"
                />
                <Radio
                  name="engagement"
                  value="non-eor"
                  selected={form.engagementModel}
                  onChange={(v) => setField('engagementModel', v)}
                  label="Non-EOR (Onboarding Only) – Contact Sales"
                />
                <Radio
                  name="engagement"
                  value="white-label"
                  selected={form.engagementModel}
                  onChange={(v) => setField('engagementModel', v)}
                  label="White Label – Operates under your studio's brand identity (Sales follow-up required)"
                />
              </div>
              {errors.engagementModel && <div className="po-error">{errors.engagementModel}</div>}

              <div className="po-section-divider"></div>
              
              <div className="po-group">
                <h4 className="po-h4">Project Details</h4>
                <p className="po-subtitle">Provide additional information about your onboarding requirements</p>
                
                <div className="po-grid3">
                  <div className="po-field">
                    <label className="po-label">
                      Expected Number of Talents 
                      <span className="req-star">*</span>
                    </label>
                    <input
                      type="number"
                      className="po-input"
                      min="1"
                      placeholder="Enter number of talents"
                      value={form.expectedTalents}
                      onChange={(e) => setField('expectedTalents', parseInt(e.target.value) || 0)}
                      required
                    />
                    {errors.expectedTalents && <div className="po-error">{errors.expectedTalents}</div>}
                  </div>

                  <div className="po-field">
                    <label className="po-label">
                      Preferred Start Date 
                      <span className="req-star">*</span>
                    </label>
                    <input
                      type="date"
                      className="po-input"
                      value={form.preferredStartDate}
                      onChange={(e) => setField('preferredStartDate', e.target.value)}
                      required
                    />
                    {errors.preferredStartDate && <div className="po-error">{errors.preferredStartDate}</div>}
                  </div>

                  <div className="po-field">
                    <label className="po-label">
                      Engagement Duration 
                      <span className="req-star">*</span>
                    </label>
                    <select
                      className="po-input po-select-dark"
                      value={form.engagementDuration}
                      onChange={(e) => setField('engagementDuration', e.target.value)}
                      required
                    >
                      <option value="Short-Term [&lt;6 months]">Short-Term [&lt;6 months]</option>
                      <option value="Mid-Term [6–12 months]">Mid-Term [6–12 months]</option>
                      <option value="Long-Term [12+ months]">Long-Term [12+ months]</option>
                      <option value="Indefinite">Indefinite</option>
                    </select>
                    {errors.engagementDuration && <div className="po-error">{errors.engagementDuration}</div>}
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* ====== STEP 2: Background ====== */}
          {step === 1 && (
            <section className="po-card">
              <h3 className="po-h3">Section 2: Background Checks &amp; Verification</h3>
              
              <div className="po-group">
                <h4 className="po-h4">Standard Background Checks</h4>
                <p className="po-subtitle">Select the background verification services you need</p>
                
                <div className="po-checklist">
                  <Check label="Identity Verification" checked={form.background.identity} onChange={(v)=>setField('background.identity', v)} />
                  <Check label="Government Social Benefit Verification" checked={form.background.govBenefits} onChange={(v)=>setField('background.govBenefits', v)} />
                  <Check label="Tax Identification Number (TIN) Verification" checked={form.background.tin} onChange={(v)=>setField('background.tin', v)} />
                  <Check label="Criminal Record Check" checked={form.background.criminal} onChange={(v)=>setField('background.criminal', v)} />
                  <Check label="Employment Verification" checked={form.background.employment} onChange={(v)=>setField('background.employment', v)} />
                  <Check label="Educational Verification" checked={form.background.education} onChange={(v)=>setField('background.education', v)} />
                  <Check label="Reference Check" checked={form.background.reference} onChange={(v)=>setField('background.reference', v)} />
                </div>
              </div>

              <div className="po-section-divider"></div>

              <div className="po-group">
                <h4 className="po-h4">Premium Add-Ons</h4>
                <p className="po-subtitle">Additional verification services with enhanced screening</p>
                
                <div className="po-checklist">
                  <Check label="Anti-Terror / Watchlist Screening (Add-On, $19.99)" checked={form.background.watchlist} onChange={(v)=>setField('background.watchlist', v)} />
                  <Check label="Social Media Screening (Add-On, $24.99)" checked={form.background.social} onChange={(v)=>setField('background.social', v)} />
                  <Check label="Credit History Check (Add-On, $29.99)" checked={form.background.credit} onChange={(v)=>setField('background.credit', v)} />
                </div>
              </div>

              <div className="po-section-divider"></div>

              <div className="po-group">
                <h4 className="po-h4">Custom Verification Requests</h4>
                <p className="po-subtitle">Specify any additional verification requirements</p>
                
                <div className="po-field">
                  <label className="po-label">
                    Additional Verification Requirements
                  </label>
                  <textarea
                    className="po-textarea"
                    rows={3}
                    placeholder="e.g., Require US Tax ID check, specific industry certifications, etc."
                    value={form.customVerificationRequests}
                    onChange={(e) => setField('customVerificationRequests', e.target.value)}
                  />
                </div>
              </div>
            </section>
          )}

          {/* ====== STEP 3: Medical ====== */}
          {step === 2 && (
            <section className="po-card">
              <h3 className="po-h3">Section 3: Medical Screening</h3>
              
              <div className="po-group">
                <h4 className="po-h4">Screening Location</h4>
                <p className="po-subtitle">Choose where the medical screening will be conducted</p>
                
                <div className="po-field">
                  <label className="po-label">
                    Medical Screening Location 
                    <span className="req-star">*</span>
                  </label>
                  <select
                    className="po-input po-select-dark"
                    value={form.medicalLocation}
                    onChange={(e) => setField('medicalLocation', e.target.value)}
                    required
                  >
                    <option value="Exodia Facility">Exodia Facility</option>
                    <option value="Client Facility">Client Facility</option>
                    <option value="Local Partner Clinic">Local Partner Clinic</option>
                  </select>
                  {errors.medicalLocation && <div className="po-error">{errors.medicalLocation}</div>}
                </div>
              </div>

              <div className="po-section-divider"></div>

              <div className="po-group">
                <h4 className="po-h4">Standard Medical Tests</h4>
                <p className="po-subtitle">Select the standard medical examinations required</p>
                
                <div className="po-checklist">
                  <Check label="CBC (Complete Blood Count)" checked={form.medical.cbc} onChange={(v)=>setField('medical.cbc', v)} />
                  <Check label="Urinalysis" checked={form.medical.urinalysis} onChange={(v)=>setField('medical.urinalysis', v)} />
                  <Check label="Chest X-ray" checked={form.medical.xray} onChange={(v)=>setField('medical.xray', v)} />
                  <Check label="Stool Examination" checked={form.medical.stool} onChange={(v)=>setField('medical.stool', v)} />
                  <Check label="Drug Test (Meth & THC)" checked={form.medical.drug} onChange={(v)=>setField('medical.drug', v)} />
                  <Check label="Complete Physical Examination" checked={form.medical.pe} onChange={(v)=>setField('medical.pe', v)} />
                </div>
              </div>

              <div className="po-section-divider"></div>

              <div className="po-group">
                <h4 className="po-h4">Medical Add-Ons</h4>
                <p className="po-subtitle">Additional specialized medical screenings</p>
                
                <div className="po-checklist">
                  <Check label="Psychological Exam (Add-On, $49.99)" checked={form.addons.psychologicalExam} onChange={(v)=>setField('addons.psychologicalExam', v)} />
                  <Check label="HIV & Hepatitis B Screening (Add-On, $59.99)" checked={form.addons.hivHepBScreening} onChange={(v)=>setField('addons.hivHepBScreening', v)} />
                </div>
              </div>
            </section>
          )}

          {/* ====== STEP 4: Employment Docs ====== */}
          {step === 3 && (
            <section className="po-card">
              <h3 className="po-h3">Section 4: Employment Documents (Auto-Included, Confirm)</h3>
              
              <div className="po-group">
                <h4 className="po-h4">Contract Jurisdiction</h4>
                <p className="po-subtitle">Select the legal jurisdiction for employment contracts</p>
                
                <div className="po-field">
                  <label className="po-label">
                    Contract Jurisdiction 
                    <span className="req-star">*</span>
                  </label>
                  <select
                    className="po-input po-select-dark"
                    value={form.jurisdiction}
                    onChange={(e) => setField('jurisdiction', e.target.value)}
                    required
                  >
                    <option value="Philippines">Philippines</option>
                    <option value="US">US</option>
                    <option value="EU">EU</option>
                    <option value="Japan">Japan</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.jurisdiction && <div className="po-error">{errors.jurisdiction}</div>}
                </div>

                {form.jurisdiction === 'Other' && (
                  <div className="po-field">
                    <label className="po-label">
                      Please specify jurisdiction
                    </label>
                    <input
                      className="po-input"
                      placeholder="Enter jurisdiction details..."
                      value={form.jurisdictionOther}
                      onChange={(e) => setField('jurisdictionOther', e.target.value)}
                      required
                    />
                    {errors.jurisdictionOther && <div className="po-error">{errors.jurisdictionOther}</div>}
                  </div>
                )}
              </div>

              <div className="po-section-divider"></div>

              <div className="po-group">
                <h4 className="po-h4">Auto-Included Documents</h4>
                <p className="po-subtitle">These documents are automatically included in all onboarding packages</p>
                
                <div className="po-checklist">
                  {[
                    'Offer Letter','Employment Contract','Confidentiality & IP Agreement','Data Privacy Consent',
                    'Code of Conduct Acknowledgment','Tax & Payroll Compliance Registration','Emergency Contact Form'
                  ].map((doc) => (
                    <label className="po-check po-locked" key={doc} title="Auto-included">
                      <input type="checkbox" checked readOnly />
                      <span className="po-checkmark" /> {doc}
                    </label>
                  ))}
                </div>
              </div>

              <div className="po-section-divider"></div>

              <div className="po-group">
                <h4 className="po-h4">Client-Specific Policy Upload</h4>
                <p className="po-subtitle">Upload any client-provided documents or policies</p>
                
                <div className="po-field">
                  <label className="po-label">
                    Upload Client Policies/Documents
                  </label>
                  <input
                    type="file"
                    className="po-input"
                    accept=".pdf,.doc,.docx,.txt"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setField('clientPolicyFile', file);
                      }
                    }}
                  />
                  {form.clientPolicyFile && (
                    <div className="po-help">
                      <strong>Selected:</strong> {form.clientPolicyFile.name}
                    </div>
                  )}
                  <div className="po-help">Upload client-provided NDAs, compliance rules, or policy manuals (PDF, DOC, DOCX, TXT)</div>
                </div>
              </div>
            </section>
          )}

          {/* ====== STEP 5: Setup ====== */}
          {step === 4 && (
            <section className="po-card">
              <h3 className="po-h3">Section 5: Work Enablement / Setup</h3>

              <div className="po-group">
                <h4 className="po-h4">Work Arrangement & Schedule</h4>
                <p className="po-subtitle">Configure the work environment and schedule requirements</p>
                
                <div className="po-grid3">
                  <div className="po-field">
                    <label className="po-label">
                      Work Arrangement 
                      <span className="req-star">*</span>
                    </label>
                    <select
                      className="po-input po-select-dark"
                      value={form.workArrangement}
                      onChange={(e) => setField('workArrangement', e.target.value)}
                      required
                    >
                      <option value="Onsite">Onsite</option>
                      <option value="Hybrid">Hybrid</option>
                      <option value="Remote">Remote</option>
                    </select>
                    {errors.workArrangement && <div className="po-error">{errors.workArrangement}</div>}
                  </div>

                  <div className="po-field">
                    <label className="po-label">
                      Shift Requirement 
                      <span className="req-star">*</span>
                    </label>
                    <select
                      className="po-input po-select-dark"
                      value={form.shiftRequirement}
                      onChange={(e) => setField('shiftRequirement', e.target.value)}
                      required
                    >
                      <option value="Day">Day</option>
                      <option value="Night">Night</option>
                      <option value="Flexible">Flexible</option>
                    </select>
                    {errors.shiftRequirement && <div className="po-error">{errors.shiftRequirement}</div>}
                  </div>

                  <div className="po-field">
                    <label className="po-label">
                      Tools Provided by Client 
                      <span className="req-star">*</span>
                    </label>
                    <select
                      className="po-input po-select-dark"
                      value={form.toolsProvidedByClient}
                      onChange={(e) => setField('toolsProvidedByClient', e.target.value)}
                      required
                    >
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                    {errors.toolsProvidedByClient && <div className="po-error">{errors.toolsProvidedByClient}</div>}
                  </div>
                </div>

                {form.toolsProvidedByClient === 'Yes' && (
                  <div className="po-field">
                    <label className="po-label">
                      Please specify tools provided
                    </label>
                    <textarea
                      className="po-textarea"
                      rows={3}
                      placeholder="List the tools, software, or equipment provided by the client..."
                      value={form.toolsProvidedDetails}
                      onChange={(e) => setField('toolsProvidedDetails', e.target.value)}
                      required
                    />
                    {errors.toolsProvidedDetails && <div className="po-error">{errors.toolsProvidedDetails}</div>}
                  </div>
                )}
              </div>

              <div className="po-section-divider"></div>

              <div className="po-group">
                <h4 className="po-h4">Workstation Setup</h4>
                <p className="po-subtitle">Choose the workstation configuration for your team</p>
                
                <div className="po-grid1">
                  <Radio name="workstation" value="standard" selected={form.workstation} onChange={(v)=>setField('workstation', v)}
                    label="Standard Setup (general-purpose workstation, standard peripherals, pre-installed basic apps)" />
                  <Radio name="workstation" value="premium" selected={form.workstation} onChange={(v)=>setField('workstation', v)}
                    label="Premium Setup (high-performance workstation, full creative suite, custom configuration)" />
                </div>
                {errors.workstation && <div className="po-error">{errors.workstation}</div>}
              </div>

              <div className="po-section-divider"></div>

              <div className="po-group">
                <h4 className="po-h4">IT & System Access</h4>
                <p className="po-subtitle">Select the IT access level for your team</p>
                
                <div className="po-grid1">
                  <Radio name="itAccess" value="standard" selected={form.itAccess} onChange={(v)=>setField('itAccess', v)}
                    label="Standard IT Access (business email, core tools, shared storage, standard security)" />
                  <Radio name="itAccess" value="premium" selected={form.itAccess} onChange={(v)=>setField('itAccess', v)}
                    label="Premium IT Access (custom tools, enterprise-grade cybersecurity, segmented networks, advanced access controls)" />
                </div>
                {errors.itAccess && <div className="po-error">{errors.itAccess}</div>}
              </div>
            </section>
          )}

          {/* ====== STEP 6: Add-ons ====== */}
          {step === 5 && (
            <section className="po-card">
              <h3 className="po-h3">Section 6: Optional Add-Ons</h3>
              
              <div className="po-group">
                <h4 className="po-h4">Premium Add-Ons</h4>
                <p className="po-subtitle">Enhance your onboarding experience with these optional services</p>
                
                <div className="po-checklist">
                  <Check
                    label="Fast-Track Onboarding (cut from 30 days → 14 days)"
                    checked={form.addons.fastTrack}
                    onChange={(v)=>setField('addons.fastTrack', v)}
                  />
                  <Check
                    label="White Label Branding (Sales engagement required)"
                    checked={form.addons.whiteLabelBranding}
                    onChange={(v)=>setField('addons.whiteLabelBranding', v)}
                  />
                </div>
                
                <div className="po-help">
                  <strong>Fast-Track pricing:</strong> EOR Standard $50 • EOR Premium $20 • Non-EOR $100
                </div>
              </div>

              <div className="po-section-divider"></div>

              <div className="po-group">
                <h4 className="po-h4">Custom Add-On Requests</h4>
                <p className="po-subtitle">Specify any additional requirements not covered above</p>
                
                <div className="po-field">
                  <label className="po-label">
                    Additional Add-On Requirements
                  </label>
                  <textarea
                    className="po-textarea"
                    rows={3}
                    placeholder="Specify any custom add-ons or special requirements not listed above..."
                    value={form.customAddOnRequests}
                    onChange={(e) => setField('customAddOnRequests', e.target.value)}
                  />
                </div>
              </div>
            </section>
          )}

          {/* ====== STEP 7: Notes ====== */}
          {step === 6 && (
            <section className="po-card">
              <h3 className="po-h3">Section 7: Notes / Special Instructions</h3>
              
              <div className="po-group">
                <h4 className="po-h4">Compliance & Setup Requirements</h4>
                <p className="po-subtitle">Specify the essential compliance and setup needs for this onboarding</p>
                
                <div className="po-field">
                  <label className="po-label">
                    Top 3 Must-Have Compliance/Setup Needs 
                    <span className="req-star">*</span>
                  </label>
                  <textarea
                    className="po-textarea"
                    rows={3}
                    placeholder="List the top 3 essential compliance or setup requirements for this onboarding..."
                    value={form.complianceSetupNeeds}
                    onChange={(e) => setField('complianceSetupNeeds', e.target.value)}
                    required
                  />
                  {errors.complianceSetupNeeds && <div className="po-error">{errors.complianceSetupNeeds}</div>}
                </div>
              </div>

              <div className="po-section-divider"></div>

              <div className="po-group">
                <h4 className="po-h4">Point of Contact for Onboarding</h4>
                <p className="po-subtitle">Provide contact information for the onboarding coordinator</p>
                
                <div className="po-grid3">
                  <div className="po-field">
                    <label className="po-label">
                      Full Name 
                      <span className="req-star">*</span>
                    </label>
                    <input
                      className="po-input"
                      placeholder="Enter full name"
                      value={form.pointOfContact.fullName}
                      onChange={(e) => setField('pointOfContact.fullName', e.target.value)}
                      required
                    />
                    {errors.pointOfContactFullName && <div className="po-error">{errors.pointOfContactFullName}</div>}
                  </div>

                  <div className="po-field">
                    <label className="po-label">
                      Email Address 
                      <span className="req-star">*</span>
                    </label>
                    <input
                      type="email"
                      className="po-input"
                      placeholder="Enter email address"
                      value={form.pointOfContact.email}
                      onChange={(e) => setField('pointOfContact.email', e.target.value)}
                      required
                    />
                    {errors.pointOfContactEmail && <div className="po-error">{errors.pointOfContactEmail}</div>}
                  </div>

                  <div className="po-field">
                    <label className="po-label">
                      Phone/WhatsApp 
                      <span className="req-star">*</span>
                    </label>
                    <input
                      type="tel"
                      className="po-input"
                      placeholder="Enter phone number"
                      value={form.pointOfContact.phoneWhatsapp}
                      onChange={(e) => setField('pointOfContact.phoneWhatsapp', e.target.value)}
                      required
                    />
                    {errors.pointOfContactPhone && <div className="po-error">{errors.pointOfContactPhone}</div>}
                  </div>
                </div>
              </div>

              <div className="po-section-divider"></div>

              <div className="po-group">
                <h4 className="po-h4">Additional Notes</h4>
                <p className="po-subtitle">Add any special instructions or additional requirements</p>
                
                <div className="po-field">
                  <label className="po-label">
                    Special Instructions
                  </label>
                  <textarea
                    className="po-textarea"
                    rows={4}
                    placeholder="e.g., Need VR experience, anime-style art, specific client requirements..."
                    value={form.notes}
                    onChange={(e)=>setField('notes', e.target.value)}
                  />
                </div>
              </div>
            </section>
          )}

          {/* ---------- WIZARD FOOTER (your exact block) ---------- */}
          <div className="po-footer po-footer--wizard">
            <div className="po-footer-left">
              <button type="button" className="po-btn po-btn-ghost" onClick={saveDraft}>Save Draft</button>
              <button type="button" className="po-btn po-btn-ghost" onClick={clearDraft}>
                Clear Draft
              </button>
            </div>
            <div className="po-footer-right">
              <button type="button" className="po-btn po-btn-muted" onClick={onClose}>Cancel</button>
              {step > 0 && (
                <button type="button" className="po-btn" onClick={goBack}>Back</button>
              )}
              {step < totalSteps - 1 ? (
                <button
                  type="button"
                  className="po-btn po-btn-primary"
                  onClick={(e) => { e.preventDefault(); goNext(); }}
                  formNoValidate
                >
                  Next
                </button>
              ) : (
                <button type="submit" className="po-btn po-btn-primary">Submit</button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
